import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import api from '../../services/api';
import { organizationsAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';
import { formatRoleLabel } from '../../utils/roles';

const roleOptions = [
  {
    value: 'org_admin',
    label: 'Headquarters Admin',
    description: 'Organization-wide headquarters oversight across all facilities. Requires Stratel admin approval before invitation is sent.',
  },
  {
    value: 'org_team',
    label: 'Headquarters Team',
    description: 'Organization-wide headquarters team member with cross-facility operational visibility, but no billing or team administration rights.',
  },
  {
    value: 'facility_admin',
    label: 'Facility Admin',
    description: 'Runs day-to-day operations for assigned facilities, including facility team access and service activity.',
  },
  {
    value: 'facility_manager',
    label: 'Facility Manager',
    description: 'Facility-scoped operational lead for assigned facilities.',
  },
  {
    value: 'facility_worker',
    label: 'Facility Team Member (Training)',
    description: 'Facility staff member with limited training and operational access.',
  },
];

export default function TeamMembersPage() {
  const { user } = useAuthStore();
  const [members, setMembers] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [invites, setInvites] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [loadingFacilities, setLoadingFacilities] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ email: '', role: '', adminInviteReason: '', facilityIds: [] });
  const [roleDrafts, setRoleDrafts] = useState({});
  const [facilityDrafts, setFacilityDrafts] = useState({});
  const [updatingRoleId, setUpdatingRoleId] = useState('');
  const [togglingStatusId, setTogglingStatusId] = useState('');
  const [showInviteFacilityPicker, setShowInviteFacilityPicker] = useState(false);
  const [expandedMemberFacilityPickerId, setExpandedMemberFacilityPickerId] = useState('');

  const organizationId = user?.organizationId?._id || user?.organizationId;
  const isOrgAdmin = user?.role === 'org_admin';
  const isFacilityAdmin = user?.role === 'facility_admin';
  const isHeadquartersRole = (role) => ['org_admin', 'org_team'].includes(role);
  const resolvedCurrentUserProfile = currentUserProfile || user;
  const currentUserIdentifiers = Array.from(new Set([
    String(user?._id || ''),
    String(resolvedCurrentUserProfile?._id || ''),
    String(user?.email || '').trim().toLowerCase(),
    String(resolvedCurrentUserProfile?.email || '').trim().toLowerCase(),
  ].filter(Boolean)));
  const matchedCurrentUserMemberRecord = members.find((member) => {
    const memberId = String(member?._id || '');
    const memberEmail = String(member?.email || '').trim().toLowerCase();
    return currentUserIdentifiers.includes(memberId) || currentUserIdentifiers.includes(memberEmail);
  });
  const fallbackFacilityAdminRecord = isFacilityAdmin
    ? members.find((member) => (
        member?.role === 'facility_admin' &&
        Array.isArray(member?.clientStaffProfile?.facilityIds) &&
        member.clientStaffProfile.facilityIds.length > 0
      ))
    : null;
  const currentUserMemberRecord = matchedCurrentUserMemberRecord || fallbackFacilityAdminRecord;
  const currentUserMemberFacilityIds = Array.isArray(currentUserMemberRecord?.clientStaffProfile?.facilityIds)
    ? currentUserMemberRecord.clientStaffProfile.facilityIds.map((id) => String(id))
    : Array.isArray(facilityDrafts[currentUserMemberRecord?._id])
      ? facilityDrafts[currentUserMemberRecord._id].map((id) => String(id))
      : [];
  const currentUserFacilityIds = Array.isArray(currentUserMemberRecord?.clientStaffProfile?.facilityIds)
    ? currentUserMemberRecord.clientStaffProfile.facilityIds.map((id) => String(id))
    : currentUserMemberFacilityIds.length > 0
      ? currentUserMemberFacilityIds
    : Array.isArray(resolvedCurrentUserProfile?.clientStaffProfile?.facilityIds)
      ? resolvedCurrentUserProfile.clientStaffProfile.facilityIds.map((id) => String(id))
      : [];
  const currentUserFacilityNames = Array.isArray(currentUserMemberRecord?.clientStaffProfile?.facilityNames)
    ? currentUserMemberRecord.clientStaffProfile.facilityNames
    : Array.isArray(resolvedCurrentUserProfile?.clientStaffProfile?.facilityNames)
      ? resolvedCurrentUserProfile.clientStaffProfile.facilityNames
      : [];
  const fallbackScopedFacilities = currentUserFacilityIds.map((id, index) => ({
    _id: id,
    name: currentUserFacilityNames[index] || currentUserFacilityNames[0] || 'Assigned Facility',
  }));
  const effectiveScopedFacilities = (
    facilities.filter((facility) => currentUserFacilityIds.includes(String(facility._id))).length > 0
      ? facilities.filter((facility) => currentUserFacilityIds.includes(String(facility._id)))
      : fallbackScopedFacilities
  );
  const assignableFacilities = isFacilityAdmin
    ? (effectiveScopedFacilities.length > 0 ? [effectiveScopedFacilities[0]] : [])
    : facilities;
  const currentUserSingleFacilityName = currentUserFacilityNames[0] || assignableFacilities[0]?.name || '';
  const isSingleFacilityScope = isFacilityAdmin || assignableFacilities.length === 1;
  const hasResolvedCurrentUserFacilityMapping = currentUserFacilityIds.length > 0 || Boolean(currentUserSingleFacilityName);
  const isFacilityAdminMissingFacilityMapping = isFacilityAdmin && !hasResolvedCurrentUserFacilityMapping;
  const availableRoleOptions = roleOptions.filter((role) => (
    isOrgAdmin
      ? isHeadquartersRole(role.value)
      : ['facility_admin', 'facility_manager', 'facility_worker'].includes(role.value)
  ));
  const defaultRoleValue = availableRoleOptions[0]?.value || '';
  const isFacilityScopedRole = (role) => ['facility_admin', 'facility_manager', 'facility_worker'].includes(role);

  const summarizeFacilities = (facilityIds = [], facilityNames = []) => {
    if (!facilityIds.length) return 'No facilities selected';
    if (facilityNames.length === 0) return `${facilityIds.length} facility${facilityIds.length === 1 ? '' : 'ies'} selected`;
    if (facilityNames.length === 1) return facilityNames[0];
    return `${facilityNames[0]} + ${facilityNames.length - 1} more`;
  };

  const facilityNamesForIds = (facilityIds = []) => (
    assignableFacilities
      .filter((facility) => facilityIds.includes(String(facility._id)))
      .map((facility) => facility.name)
  );

  useEffect(() => {
    if (!defaultRoleValue) return;
    setForm((prev) => {
      if (prev.role && availableRoleOptions.some((role) => role.value === prev.role)) return prev;
      return {
        ...prev,
        role: defaultRoleValue,
        facilityIds: isFacilityAdmin && isFacilityScopedRole(defaultRoleValue)
          ? (isSingleFacilityScope && assignableFacilities[0]?._id
              ? [String(assignableFacilities[0]._id)]
              : currentUserFacilityIds)
          : [],
      };
    });
  }, [defaultRoleValue]);

  useEffect(() => {
    if (!isFacilityAdmin || !isFacilityScopedRole(form.role) || form.facilityIds.length > 0 || assignableFacilities.length === 0) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      facilityIds: isSingleFacilityScope
        ? (assignableFacilities[0]?._id ? [String(assignableFacilities[0]._id)] : [])
        : currentUserFacilityIds,
    }));
  }, [isFacilityAdmin, form.role, form.facilityIds.length, currentUserFacilityIds.join('|'), isSingleFacilityScope, assignableFacilities.length]);

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const res = await api.get('/users');
      const list = res.data?.users || res.data || [];
      const scopedList = isOrgAdmin
        ? list.filter((member) => isHeadquartersRole(member.role))
        : list;
      setMembers(scopedList);
      setFacilityDrafts(
        Object.fromEntries(
          scopedList.map((member) => [
            member._id,
            Array.isArray(member?.clientStaffProfile?.facilityIds)
              ? member.clientStaffProfile.facilityIds.map((id) => String(id))
              : [],
          ])
        )
      );
    } catch (error) {
      toast.error(error.message || 'Failed to load facility team members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadCurrentUserProfile = async () => {
    if (!user?._id) return;
    try {
      const res = await api.get(`/users/${user._id}`);
      const resolvedUser = res.data?.user || null;
      setCurrentUserProfile(resolvedUser);
    } catch (_error) {
      setCurrentUserProfile(null);
    }
  };

  const loadFacilities = async () => {
    setLoadingFacilities(true);
    try {
      const res = await organizationsAPI.getMyFacilities();
      const list = res.data?.facilities || [];
      setFacilities(list);
    } catch (error) {
      toast.error(error.message || 'Failed to load facilities');
    } finally {
      setLoadingFacilities(false);
    }
  };

  const loadInvites = async () => {
    setLoadingInvites(true);
    try {
      const res = await api.get('/invitations');
      const rows = res.data?.invitations || [];
      setInvites(
        rows.filter((row) =>
          ['pending', 'pending_approval'].includes(row.status) &&
          (isOrgAdmin ? isHeadquartersRole(row.role) : !isHeadquartersRole(row.role))
        )
      );
    } catch (error) {
      toast.error(error.message || 'Failed to load invitations');
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    loadCurrentUserProfile();
    loadMembers();
    loadInvites();
    loadFacilities();
  }, []);

  const toggleIds = (currentIds = [], facilityId) => {
    const normalizedId = String(facilityId || '');
    if (!normalizedId) return currentIds;
    return currentIds.includes(normalizedId)
      ? currentIds.filter((id) => id !== normalizedId)
      : [...currentIds, normalizedId];
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (!organizationId) {
      toast.error('Missing organization information.');
      return;
    }
    if (form.role === 'org_admin' && String(form.adminInviteReason || '').trim().length < 10) {
      toast.error('Please provide a clear reason (at least 10 characters) for headquarters admin invite.');
      return;
    }
    if (!['org_admin', 'org_team'].includes(form.role) && assignableFacilities.length > 0 && (form.facilityIds || []).length === 0) {
      toast.error('Assign at least one facility for facility-scoped users.');
      return;
    }

    setSending(true);
    try {
      await api.post('/invitations', {
        email,
        role: form.role,
        organizationId,
        adminInviteReason: form.role === 'org_admin' ? String(form.adminInviteReason || '').trim() : undefined,
        facilityIds: ['org_admin', 'org_team'].includes(form.role) ? [] : form.facilityIds,
      });
      toast.success(form.role === 'org_admin' ? 'Invite submitted for Stratel approval' : 'Invitation sent');
      setForm({
        email: '',
        role: defaultRoleValue,
        adminInviteReason: '',
        facilityIds: isSingleFacilityScope && defaultRoleValue && isFacilityScopedRole(defaultRoleValue)
          ? [String(assignableFacilities[0]._id)]
          : [],
      });
      setShowInviteFacilityPicker(false);
      loadInvites();
    } catch (error) {
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const formatRole = (role) => formatRoleLabel(role);

  const toggleUserStatus = async (member) => {
    const nextActive = member.isActive === false;
    const action = nextActive ? 'Reactivate' : 'Deactivate';
    const confirmationKey = nextActive ? 'REACTIVATE' : 'DEACTIVATE';
    const answer = window.prompt(
      `Type \"${confirmationKey}\" to ${action.toLowerCase()} ${member.email}.`,
    );
    if (String(answer || '').trim() !== confirmationKey) return;

    setTogglingStatusId(member._id);
    try {
      await api.put(`/users/${member._id}`, { isActive: nextActive });
      toast.success(nextActive ? 'User reactivated' : 'User deactivated');
      setMembers((prev) =>
        prev.map((m) => (m._id === member._id ? { ...m, isActive: nextActive } : m))
      );
    } catch (error) {
      toast.error(error.message || 'Failed to update user status');
    } finally {
      setTogglingStatusId('');
    }
  };

  const saveRoleChange = async (member) => {
    const nextRole = roleDrafts[member._id];
    const resolvedRole = nextRole || member.role;
    const facilityIds = facilityDrafts[member._id] || [];
    const currentFacilityIds = Array.isArray(member?.clientStaffProfile?.facilityIds)
      ? member.clientStaffProfile.facilityIds.map((id) => String(id))
      : [];
    const facilitiesChanged =
      facilityIds.length !== currentFacilityIds.length ||
      facilityIds.some((id) => !currentFacilityIds.includes(String(id)));
    const roleChanged = resolvedRole !== member.role;
    if (!roleChanged && !facilitiesChanged) return;
    if (!['org_admin', 'org_team'].includes(resolvedRole) && assignableFacilities.length > 0 && facilityIds.length === 0) {
      toast.error('Assign at least one facility before saving this role.');
      return;
    }
    setUpdatingRoleId(member._id);
    try {
      await api.put(`/users/${member._id}`, {
        role: resolvedRole,
        facilityIds: ['org_admin', 'org_team'].includes(resolvedRole) ? [] : facilityIds,
      });
      toast.success('Role updated');
      setMembers((prev) => prev.map((m) => (
        m._id === member._id
          ? {
              ...m,
              role: resolvedRole,
              clientStaffProfile: {
                ...(m.clientStaffProfile || {}),
                facilityIds: ['org_admin', 'org_team'].includes(resolvedRole) ? [] : facilityIds,
                facilityNames: (['org_admin', 'org_team'].includes(resolvedRole) ? [] : assignableFacilities)
                  .filter((facility) => facilityIds.includes(String(facility._id)))
                  .map((facility) => facility.name),
              },
            }
          : m
      )));
    } catch (error) {
      toast.error(error.message || 'Failed to update role');
    } finally {
      setUpdatingRoleId('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">
            {isOrgAdmin ? 'Organization Team Directory' : 'Facility Team Members'}
          </h1>
          <p className="mt-1 text-secondary-600">
            {isOrgAdmin
              ? 'Manage headquarters users only. Facility hiring and deactivation stays with facility admins.'
              : 'Invite teammates and manage only the users assigned to your facility scope.'}
          </p>
        </div>
      </div>

      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-2 text-secondary-900 mb-4">
          <UserPlus size={18} className="text-primary-600" />
          <h2 className="font-semibold">Invite a teammate</h2>
        </div>
        {isFacilityAdminMissingFacilityMapping && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Your account is not mapped to a facility yet. A Stratel or headquarters admin needs to attach this facility admin profile to at least one facility before you can see or manage facility team members.
          </div>
        )}
        {isFacilityAdmin && !isFacilityAdminMissingFacilityMapping && currentUserSingleFacilityName && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            Your assigned facility: {currentUserSingleFacilityName}
          </div>
        )}
        <form onSubmit={handleInvite} className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="label">Email</label>
            <input
              type="email"
              className="input"
              placeholder="teammate@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="w-full sm:w-56 sm:flex-none">
            <label className="label">Role</label>
            <select
              className="input"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {availableRoleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          {form.role === 'org_admin' ? (
            <div className="w-full">
              <label className="label">Reason (required for headquarters admin invite)</label>
              <textarea
                className="input min-h-[84px]"
                placeholder="Explain why this user needs headquarters-wide admin privileges..."
                value={form.adminInviteReason}
                onChange={(e) => setForm({ ...form, adminInviteReason: e.target.value })}
              />
            </div>
          ) : null}
          {isFacilityScopedRole(form.role) ? (
            <div className="flex-1 min-w-[200px]">
              <label className="label">Facility Access</label>
              {loadingFacilities ? (
                <div className="text-sm text-secondary-500">Loading facilities...</div>
              ) : assignableFacilities.length === 0 ? (
                <div className="text-sm text-secondary-500">
                  {isFacilityAdminMissingFacilityMapping
                    ? 'You do not currently have facility access in your profile, so no assignable facilities are available here yet.'
                    : 'No facilities are available in your current scope yet. Add or sync facilities before inviting facility-scoped users.'}
                </div>
              ) : isSingleFacilityScope ? (
                <div className="text-sm text-secondary-700">
                  Assigned facility: <span className="font-medium">{currentUserSingleFacilityName}</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="btn-secondary py-2 text-sm"
                      onClick={() => setShowInviteFacilityPicker((prev) => !prev)}
                    >
                      {showInviteFacilityPicker ? 'Hide Facilities' : 'Choose Facilities'}
                    </button>
                    <span className="text-sm text-secondary-600">
                      {summarizeFacilities(form.facilityIds, facilityNamesForIds(form.facilityIds))}
                    </span>
                  </div>
                  {showInviteFacilityPicker && (
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-secondary-200 bg-secondary-50 p-3">
                      <div className="flex flex-wrap gap-2">
                        {assignableFacilities.map((facility) => (
                          <label
                            key={facility._id}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm cursor-pointer ${
                              form.facilityIds.includes(String(facility._id))
                                ? 'bg-primary-50 border-primary-300 text-primary-700'
                                : 'bg-white border-secondary-200 text-secondary-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={form.facilityIds.includes(String(facility._id))}
                              onChange={() => setForm((prev) => ({
                                ...prev,
                                facilityIds: toggleIds(prev.facilityIds || [], facility._id),
                              }))}
                            />
                            <span>{facility.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : form.role ? (
            <div className="flex-1 min-w-[200px]">
              <label className="label">Facility Access</label>
              <div className="text-sm text-secondary-500">Not applicable. Headquarters roles have organization-wide visibility.</div>
            </div>
          ) : null}
          <div className="flex items-end justify-end w-full">
            <button
              type="submit"
              disabled={sending || (isFacilityAdmin && assignableFacilities.length === 0)}
              className="btn-primary w-full sm:w-auto"
            >
              {sending ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
        <p className="mt-3 text-sm text-secondary-500">
          Invited teammates will receive a secure link to create their account.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {availableRoleOptions.map((role) => (
            <span
              key={role.value}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-50 text-secondary-600 border border-secondary-200"
              title={role.description}
            >
              <span className="font-medium text-secondary-700">{role.label}</span>
              <span className="text-secondary-400">Hover for details</span>
            </span>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-secondary-900">Current Team</h2>
        </div>
        <div className="card-body p-0">
          {loadingMembers ? (
            <div className="p-6 text-center text-secondary-500">
              {isOrgAdmin ? 'Loading organization team members...' : 'Loading facility team members...'}
            </div>
          ) : members.length === 0 ? (
            <div className="p-6 text-center text-secondary-500">
              {isFacilityAdmin
                ? (isFacilityAdminMissingFacilityMapping
                    ? 'This facility admin account is not mapped to any facility yet.'
                    : 'No facility team members found for your assigned facilities.')
                : 'No team members found.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Role</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Facilities</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Role Management</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {members.map((member) => (
                    <tr key={member._id} className="hover:bg-secondary-50">
                      <td data-label="Name" className="px-6 py-4 text-secondary-900 font-medium">
                        {member.firstName} {member.lastName}
                      </td>
                      <td data-label="Email" className="px-6 py-4 text-secondary-600 break-all">{member.email}</td>
                      <td data-label="Role" className="px-6 py-4">
                        <span
                          className="badge-secondary"
                          title={roleOptions.find((role) => role.value === member.role)?.description}
                        >
                          {formatRole(member.role)}
                        </span>
                      </td>
                      <td data-label="Facilities" className="px-6 py-4 text-sm text-secondary-600">
                        {['org_admin', 'org_team'].includes(member.role)
                          ? 'All organization facilities'
                          : summarizeFacilities(
                              Array.isArray(member?.clientStaffProfile?.facilityIds)
                                ? member.clientStaffProfile.facilityIds.map((id) => String(id))
                                : [],
                              member.clientStaffProfile?.facilityNames || []
                            )}
                      </td>
                      <td data-label="Role management" className="px-6 py-4">
                        {member.role === 'org_admin' ? (
                          <span className="text-xs text-secondary-500">
                            Managed via org-admin approval workflow
                          </span>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <select
                                className="input py-1.5 text-sm min-w-[180px]"
                                value={roleDrafts[member._id] || member.role}
                              onChange={(e) => setRoleDrafts((prev) => ({ ...prev, [member._id]: e.target.value }))}
                            >
                                {isOrgAdmin ? (
                                  <option value="org_team">Headquarters Team</option>
                                ) : (
                                  <>
                                    <option value="facility_admin">Facility Admin</option>
                                    <option value="facility_manager">Facility Manager</option>
                                    <option value="facility_worker">Facility Team Member (Training)</option>
                                  </>
                                )}
                              </select>
                              <button
                                type="button"
                                className="btn-secondary py-1.5 text-sm"
                                disabled={updatingRoleId === member._id}
                                onClick={() => saveRoleChange(member)}
                              >
                                {updatingRoleId === member._id ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                            {(['org_admin', 'org_team'].includes(roleDrafts[member._id] || member.role)) ? (
                              <div className="text-xs text-secondary-500">Org-wide access across all facilities.</div>
                            ) : assignableFacilities.length > 0 ? (
                              <div className="space-y-2">
                                {isFacilityAdmin ? (
                                  <div className="text-xs text-secondary-500">
                                    This user will stay within your assigned facility: {currentUserSingleFacilityName}
                                  </div>
                                ) : isSingleFacilityScope ? (
                                  <div className="text-xs text-secondary-500">
                                    Assigned facility: {currentUserSingleFacilityName}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      className="btn-secondary py-1.5 text-sm"
                                      onClick={() => setExpandedMemberFacilityPickerId((prev) => prev === member._id ? '' : member._id)}
                                    >
                                      {expandedMemberFacilityPickerId === member._id ? 'Hide Facilities' : 'Choose Facilities'}
                                    </button>
                                    <span className="text-xs text-secondary-500">
                                      {summarizeFacilities(
                                        facilityDrafts[member._id] || [],
                                        facilityNamesForIds(facilityDrafts[member._id] || [])
                                      )}
                                    </span>
                                  </div>
                                )}
                                {!isSingleFacilityScope && expandedMemberFacilityPickerId === member._id && (
                                  <div className="max-h-48 overflow-y-auto rounded-lg border border-secondary-200 bg-secondary-50 p-3">
                                    <div className="flex flex-wrap gap-2">
                                      {assignableFacilities.map((facility) => {
                                        const currentIds = facilityDrafts[member._id] || [];
                                        const checked = currentIds.includes(String(facility._id));
                                        return (
                                          <label
                                            key={`${member._id}-${facility._id}`}
                                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs cursor-pointer ${
                                              checked
                                                ? 'bg-primary-50 border-primary-300 text-primary-700'
                                                : 'bg-white border-secondary-200 text-secondary-700'
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              className="sr-only"
                                              checked={checked}
                                              onChange={() => setFacilityDrafts((prev) => ({
                                                ...prev,
                                                [member._id]: toggleIds(prev[member._id] || [], facility._id),
                                              }))}
                                            />
                                            <span>{facility.name}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-secondary-500">
                                {currentUserSingleFacilityName
                                  ? `Assigned facility: ${currentUserSingleFacilityName}`
                                  : 'No facilities on file.'}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td data-label="Status" className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          <span className={member.isActive !== false ? 'badge-success' : 'badge-danger'}>
                            {member.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                          {isOrgAdmin && member._id !== user?._id && member.role !== 'org_admin' && (
                            <button
                              type="button"
                              className={`text-xs font-medium underline ${member.isActive !== false ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                              disabled={togglingStatusId === member._id}
                              onClick={() => toggleUserStatus(member)}
                            >
                              {togglingStatusId === member._id ? 'Updating...' : member.isActive !== false ? 'Deactivate' : 'Reactivate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-secondary-900">Pending Invitations</h2>
        </div>
        <div className="card-body p-0">
          {loadingInvites ? (
            <div className="p-6 text-center text-secondary-500">Loading invitations...</div>
          ) : invites.length === 0 ? (
            <div className="p-6 text-center text-secondary-500">No pending invitations.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Role</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Invited By</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase">Expires</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {invites.map((invite) => (
                    <tr key={invite._id} className="hover:bg-secondary-50">
                      <td data-label="Email" className="px-6 py-4 text-secondary-700 break-all">{invite.email}</td>
                      <td data-label="Role" className="px-6 py-4">
                        <span
                          className="badge-secondary"
                          title={roleOptions.find((role) => role.value === invite.role)?.description}
                        >
                          {formatRole(invite.role)}
                        </span>
                      </td>
                      <td data-label="Status" className="px-6 py-4 text-sm text-secondary-600">
                        {invite.status === 'pending_approval' ? 'Pending Stratel Approval' : 'Pending'}
                      </td>
                      <td data-label="Invited by" className="px-6 py-4 text-secondary-600">
                        {invite.invitedBy?.userId?.firstName
                          ? `${invite.invitedBy.userId.firstName} ${invite.invitedBy.userId.lastName}`
                          : invite.invitedBy?.email || 'N/A'}
                      </td>
                      <td data-label="Expires" className="px-6 py-4 text-secondary-500 text-sm">
                        {invite.expiresAt ? new Date(invite.expiresAt).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
